import React, { useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { api, unwrap } from "@/lib/api";

export default function PrivacyPage() {
  const [phone, setPhone] = useState("508-397-0721");
  useEffect(() => {
    api.get("/public/settings").then((r) => {
      const d = unwrap(r) || {};
      setPhone(d.company_phone || d.support_phone || "508-397-0721");
    }).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="privacy-page">
      <SEO
        title="Privacy Policy — Ever Clean Home"
        description="How Ever Clean Home collects, uses, stores and protects your personal information. Includes CCPA, GDPR, cookies, retention and third-party disclosures."
      />
      <Header />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 prose prose-stone">
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">Privacy Policy</h1>
        <p className="text-sm text-[#4B5563]">Last updated: {new Date().toLocaleDateString()}</p>

        <p>
          This Privacy Policy describes how <strong>Ever Clean Home</strong> ("we", "us", "our") collects,
          uses and shares information about you when you visit{" "}
          <code>everclean-booking.emergent.host</code> or book a service through our website. This document is a
          good-faith plain-language draft. It is <em>not</em> a substitute for legal advice — please consult an
          attorney before relying on it for legal compliance in your jurisdiction.
        </p>

        <h3>1. Data we collect</h3>
        <ul>
          <li><strong>Booking information</strong> — full name, email, phone number, billing/service address, ZIP code, notes about access or special needs you choose to provide.</li>
          <li><strong>Payment metadata</strong> — Stripe (our payment processor) collects your card details directly on its hosted checkout page. We never see or store the full card number, expiry or CVC. We do receive a Stripe customer ID, the last four digits of the card and a payment intent / session ID for reconciliation.</li>
          <li><strong>Usage data</strong> — IP address, browser user-agent, pages visited, referring URLs and timestamps, captured automatically by the website and by our analytics providers.</li>
          <li><strong>Cookies and local storage</strong> — we store a session token (so you stay signed in to the admin panel if applicable), your locale preference, and your booking draft so you can resume the wizard later. See section 5 for details.</li>
          <li><strong>Reviews</strong> — if you leave a review after a service we store your rating, comment and the booking number it refers to.</li>
        </ul>

        <h3>2. How we use it</h3>
        <ul>
          <li>To deliver the cleaning service you booked, dispatch the right team, route to your address and contact you about the appointment.</li>
          <li>To send transactional emails: booking confirmation, payment receipt, schedule changes and review requests after the service.</li>
          <li>To process payments and refunds via Stripe.</li>
          <li>To prevent fraud, abuse and brute-force attacks (we rate-limit login attempts by IP and log unusual activity).</li>
          <li>To improve the website — understand which pages drive bookings, which steps cause drop-off, and which zones have unmet demand.</li>
          <li>To comply with applicable accounting, tax and consumer-protection laws.</li>
        </ul>
        <p>We <strong>do not</strong> sell, rent or trade your personal information to third parties. We do not use your data for behavioural advertising on third-party platforms beyond optional analytics described below.</p>

        <h3>3. Third parties we share data with</h3>
        <ul>
          <li><strong>Stripe, Inc.</strong> — payments. Stripe's privacy policy: <a href="https://stripe.com/privacy" rel="noopener nofollow">stripe.com/privacy</a>.</li>
          <li><strong>Resend</strong> — transactional email delivery (booking confirmations, receipts). Privacy: <a href="https://resend.com/legal/privacy-policy" rel="noopener nofollow">resend.com/legal/privacy-policy</a>.</li>
          <li><strong>Google Analytics 4</strong> (optional, only active when an admin enables it) — aggregated usage analytics. Privacy: <a href="https://policies.google.com/privacy" rel="noopener nofollow">policies.google.com/privacy</a>.</li>
          <li><strong>Meta Pixel</strong> (optional, only active when an admin enables it) — conversion measurement for ad campaigns. Privacy: <a href="https://www.facebook.com/policy.php" rel="noopener nofollow">facebook.com/policy.php</a>.</li>
          <li><strong>Cloud hosting</strong> — our application servers and MongoDB database run on standard cloud infrastructure. Operators have technical access for maintenance under confidentiality obligations.</li>
        </ul>

        <h3>4. Data retention</h3>
        <p>
          We keep booking and payment records for a minimum of <strong>seven (7) years</strong> to comply with US accounting,
          tax and consumer-protection laws (IRS recordkeeping, Massachusetts Department of Revenue, Chapter 93A). After that
          period, records may be archived in encrypted backups for an additional two years and then permanently deleted.
        </p>
        <p>
          Contact-form messages are retained for <strong>two (2) years</strong>. Marketing analytics events are retained per
          the provider's defaults (Google Analytics: 14 months, configurable). Email logs are retained for one year.
        </p>

        <h3>5. Cookies and tracking technologies</h3>
        <p>We use the following cookies and local-storage keys:</p>
        <ul>
          <li><code>ec_locale</code> (localStorage) — remembers your language preference (EN/ES).</li>
          <li><code>ec_booking_draft</code> (localStorage) — saves your wizard inputs so you can resume later. Cleared after a successful booking.</li>
          <li><code>ec_token</code> (localStorage) — JWT session token, used only by admin users after sign in.</li>
          <li><code>_ga</code>, <code>_ga_*</code> (cookies) — set by Google Analytics when enabled. First-party.</li>
          <li><code>_fbp</code> (cookie) — set by Meta Pixel when enabled. First-party.</li>
          <li>Stripe sets its own cookies on its checkout page; those are governed by Stripe's policy.</li>
        </ul>
        <p>You can clear cookies and localStorage at any time from your browser settings. Doing so will sign you out of the admin panel and lose any unsaved booking draft.</p>

        <h3>6. Your rights</h3>
        <p>Regardless of where you live, you can:</p>
        <ul>
          <li>Request a copy of the personal information we hold about you.</li>
          <li>Request correction or deletion of your data.</li>
          <li>Opt out of marketing emails by replying STOP or clicking the unsubscribe link.</li>
          <li>Ask us to stop using your data for optional analytics.</li>
        </ul>
        <p>To exercise any of these rights, email <strong>bookings@everclean.com</strong> with the subject line "Privacy request". We respond within 30 days.</p>

        <h3>7. California residents (CCPA / CPRA)</h3>
        <p>
          If you reside in California, the California Consumer Privacy Act ("CCPA") as amended by the California Privacy Rights Act ("CPRA")
          grants you additional rights, including the right to know what personal information we collect about you, the right to delete
          it, the right to correct inaccurate information, the right to limit the use of sensitive personal information, and the
          right not to be discriminated against for exercising these rights. We do <strong>not</strong> sell personal information.
          To exercise your California rights, email <strong>bookings@everclean.com</strong> with "California privacy request" in the subject.
        </p>

        <h3>8. Children</h3>
        <p>Our services are intended for adults (18+). We do not knowingly collect information from anyone under 13. If you believe a child has shared personal information with us, please contact us so we can delete it.</p>

        <h3>9. Security</h3>
        <p>
          We use TLS encryption for all data in transit, hash passwords with bcrypt, scope session tokens with short expiry,
          rate-limit sensitive endpoints, and apply standard security headers (HSTS, X-Frame-Options, X-Content-Type-Options,
          Referrer-Policy, Permissions-Policy). No system is 100% secure, so we encourage you to choose a strong password
          and to notify us immediately if you suspect unauthorised access.
        </p>

        <h3 id="sms">10. SMS / Text Messaging</h3>
        <p>
          If you opt in during booking, Ever Clean Home may send you text messages about your service, sent from our
          messaging platform. <strong>No mobile information will be shared with third parties or affiliates for marketing
          or promotional purposes. All other categories exclude text messaging originator opt-in data and consent; this
          information will not be shared with any third parties.</strong>
        </p>
        <ul>
          <li><strong>What we send:</strong> a booking confirmation, a reminder the day before your service, and an alert
            when your cleaner is on the way. We do not send marketing texts.</li>
          <li><strong>Frequency:</strong> message frequency varies depending on how many bookings you make.</li>
          <li><strong>Cost:</strong> message and data rates may apply, depending on your carrier plan.</li>
          <li><strong>Opting out:</strong> reply <strong>STOP</strong> to any message to unsubscribe at any time. Reply
            <strong> HELP</strong> for help.</li>
          <li><strong>Consent is optional</strong> — you can book and pay for a cleaning without opting in to text
            messages, and opting out never affects your booking.</li>
        </ul>
        <p>See our <a href="/sms">SMS Program</a> page for the exact consent language shown at booking and sample messages.</p>

        <h3>11. Changes to this policy</h3>
        <p>We may update this Privacy Policy from time to time. We will update the "Last updated" date at the top of this page and, for material changes, notify booked customers by email.</p>

        <h3>12. Contact</h3>
        <p data-testid="privacy-contact-phone">Questions, concerns or requests? Email <strong>bookings@everclean.com</strong> or call <strong>{phone}</strong> (9 AM – 7 PM ET).</p>

        <p className="text-xs text-[#9CA3AF] mt-10">
          <em>This document is a good-faith draft and is not legal advice. Please have it reviewed by an attorney licensed in your jurisdiction before relying on it.</em>
        </p>
      </div>
      <Footer />
    </div>
  );
}
